/** Shared, dependency-free customer/operator motion. Never owns application state. */
const ease = "cubic-bezier(0.16, 1, 0.3, 1)";
const navigation =
  ".customer-app-nav, .customer-mobile-nav, .site-header .nav, #operations-navigation, .book-studio__tabs, .photo-library__views, .family-creative__trail, .bdr-mode-switch";
const folios =
  ".deal-catalog-hero__stage, .product-gallery__main, .lc93-scene__book, .sign-in-welcome, .lc-page-hero__visual, .voucher-purchase__hero aside, .admin-access__story";
const views = "main, #operations-workspace";
const overlays =
  "dialog[open], .customer-account-menu__popover, .book-home-tools-mobile, .photo-detail, .photo-selection-bar, .action-preview, .faq-page__accordion [role=region]";
const headers = ".site-header, .app-header, .commerce-header, .masthead";

type Marker = {
  element: HTMLElement;
  x: number;
  y: number;
  width: number;
  height: number;
  vertical: boolean;
  parentWidth: number;
  parentHeight: number;
  position?: string;
};

export function installInterfaceMotion(root: HTMLElement): () => void {
  const doc = root.ownerDocument;
  const win = doc.defaultView;
  if (!win) return () => {};
  const preference = win.matchMedia?.("(prefers-reduced-motion: reduce)");
  const animations = new Map<HTMLElement, Animation>();
  const markers = new Map<HTMLElement, Marker>();
  const visited = new WeakSet<HTMLElement>();
  const openedFolios = new WeakSet<HTMLElement>();
  const statusText = new WeakMap<HTMLElement, string>();
  const layouts = new Map<HTMLElement, Map<string, DOMRect>>();
  const pending = new Set<HTMLElement>([root]);
  let frame = 0;
  let disposed = false;

  const enabled = () => root.dataset.motionScope !== "homepage";
  const moving = () => enabled() && !preference?.matches && !doc.hidden;
  const visible = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      win.getComputedStyle(element).visibility !== "hidden" &&
      !element.closest("[hidden], [inert], details:not([open]) > :not(summary)")
    );
  };

  function cancel(element: HTMLElement) {
    animations.get(element)?.cancel();
    animations.delete(element);
  }

  function play(element: HTMLElement, keyframes: Keyframe[], duration: number, delay = 0) {
    if (!moving() || !visible(element) || typeof element.animate !== "function") return;
    cancel(element);
    try {
      const animation = element.animate(keyframes, { duration, delay, easing: ease, fill: "none" });
      animations.set(element, animation);
      const release = () => {
        if (animations.get(element) === animation) animations.delete(element);
      };
      void animation.finished.then(release, release);
    } catch {
      // Unsupported animation properties must never block the interface.
    }
  }

  function arrive(element: HTMLElement) {
    if (!enabled() || visited.has(element)) return;
    if (element.matches(headers)) {
      visited.add(element);
      const sprig = element.querySelector<HTMLElement>(".logo__sprig");
      if (sprig) play(sprig, [{ rotate: "-12deg" }, { rotate: "0deg" }], 360);
    } else if (element.matches(views)) {
      visited.add(element);
      const artifact = [...element.querySelectorAll<HTMLElement>(folios)].find(
        (candidate) =>
          visible(candidate) &&
          candidate.getBoundingClientRect().top < win!.innerHeight &&
          candidate.getBoundingClientRect().bottom > 0,
      );
      const owner = element.closest<HTMLElement>("main") ?? root;
      if (artifact && !openedFolios.has(owner)) {
        openedFolios.add(owner);
        visited.add(artifact);
        play(
          artifact,
          [
            { clipPath: "inset(0 42% 0 0)", opacity: 0.86 },
            { clipPath: "inset(0)", opacity: 1 },
          ],
          560,
        );
      }
      const heading = element.querySelector<HTMLElement>("h1, h2");
      if (heading) play(heading, [{ opacity: 0.82 }, { opacity: 1 }], 180);
    } else if (element.matches(folios) && visible(element)) {
      // Asynchronous artifacts receive the same authored opening, once.
      visited.add(element);
      const owner = element.closest<HTMLElement>("main") ?? root;
      if (
        openedFolios.has(owner) ||
        element.getBoundingClientRect().top >= win!.innerHeight ||
        element.getBoundingClientRect().bottom <= 0
      )
        return;
      openedFolios.add(owner);
      play(
        element,
        [
          { clipPath: "inset(0 42% 0 0)", opacity: 0.86 },
          { clipPath: "inset(0)", opacity: 1 },
        ],
        560,
      );
    } else if (element.matches(overlays) && visible(element)) {
      visited.add(element);
      const isAnswer = element.matches(".faq-page__accordion [role=region], .action-preview");
      play(
        element,
        isAnswer
          ? [
              { clipPath: "inset(0 0 18% 0)", opacity: 0.88 },
              { clipPath: "inset(0)", opacity: 1 },
            ]
          : [
              { translate: "0 8px", opacity: 0.9 },
              { translate: "0 0", opacity: 1 },
            ],
        240,
      );
    }
  }

  function updateNavigation() {
    for (const nav of root.querySelectorAll<HTMLElement>(navigation)) {
      const active = nav.querySelector<HTMLElement>(
        '[aria-current="page"], [aria-current="step"], [aria-pressed="true"]',
      );
      let marker = markers.get(nav);
      if (!enabled() || !active || !visible(nav) || !visible(active)) {
        if (marker) {
          cancel(marker.element);
          marker.element.hidden = true;
        }
        continue;
      }
      // Establish a containing block only for static navigation. Never replace
      // an incumbent fixed bottom bar, absolute mobile menu or sticky sidebar.
      let position = marker?.position;
      const navStyle = win!.getComputedStyle(nav);
      if (navStyle.position === "static" || !navStyle.position) {
        position = nav.style.position;
        nav.style.position = "relative";
      }
      const vertical =
        nav.id === "operations-navigation" ||
        (nav.id === "primary-nav" && navStyle.flexDirection === "column");
      const parent = nav.getBoundingClientRect();
      const box = active.getBoundingClientRect();
      const width = vertical
        ? 3
        : nav.matches(".customer-mobile-nav")
          ? Math.min(44, box.width)
          : box.width;
      const x = vertical
        ? Math.max(0, box.left - parent.left + nav.scrollLeft - 3)
        : box.left - parent.left + nav.scrollLeft + (box.width - width) / 2;
      const y = vertical
        ? box.top - parent.top + nav.scrollTop
        : box.bottom - parent.top + nav.scrollTop - 3;
      const height = vertical ? box.height : 3;
      if (!marker) {
        const element = doc.createElement("span");
        element.className = "lc-motion-navigation-ink";
        element.setAttribute("aria-hidden", "true");
        nav.dataset.motionNavigation = vertical ? "vertical" : "horizontal";
        nav.append(element);
        marker = {
          element,
          x,
          y,
          width,
          height,
          vertical,
          parentWidth: parent.width,
          parentHeight: parent.height,
          position,
        };
        markers.set(nav, marker);
      }
      nav.dataset.motionNavigation = vertical ? "vertical" : "horizontal";
      const { element } = marker;
      const wasHidden = element.hidden;
      const changed =
        marker.x !== x || marker.y !== y || marker.width !== width || marker.height !== height;
      const painted = element.getBoundingClientRect();
      if (changed || wasHidden || !element.style.width) {
        element.hidden = false;
        element.style.width = `${width}px`;
        element.style.height = `${height}px`;
        element.style.transform = `translate(${x}px, ${y}px)`;
      }
      const validPaint =
        painted.width > 0 &&
        painted.height > 0 &&
        painted.left >= parent.left - 1 &&
        painted.right <= parent.right + 1 &&
        painted.top >= parent.top - 1 &&
        painted.bottom <= parent.bottom + 1;
      const sameLayout =
        marker.vertical === vertical &&
        marker.parentWidth === parent.width &&
        marker.parentHeight === parent.height;
      if (!wasHidden && changed && validPaint && sameLayout) {
        play(
          element,
          [
            {
              transform: `translate(${painted.left - parent.left + nav.scrollLeft}px, ${painted.top - parent.top + nav.scrollTop}px) scale(${painted.width / width}, ${painted.height / height})`,
            },
            { transform: `translate(${x}px, ${y}px) scale(1, 1)` },
          ],
          260,
        );
      } else if (changed || wasHidden) {
        cancel(element);
      }
      Object.assign(marker, {
        x,
        y,
        width,
        height,
        vertical,
        parentWidth: parent.width,
        parentHeight: parent.height,
        position,
      });
    }
    for (const [nav, marker] of markers) {
      if (!root.contains(nav) || !enabled()) {
        cancel(marker.element);
        marker.element.remove();
        delete nav.dataset.motionNavigation;
        if (marker.position !== undefined && nav.style.position === "relative")
          nav.style.position = marker.position;
        markers.delete(nav);
      }
    }
  }

  function hydrate(branch: HTMLElement) {
    if (!root.contains(branch) && branch !== root) return;
    arrive(branch);
    for (const element of branch.querySelectorAll<HTMLElement>(
      `${headers}, ${views}, ${folios}, ${overlays}`,
    ))
      arrive(element);
    const statuses = branch.matches('[role="status"], [role="alert"]')
      ? [branch]
      : [...branch.querySelectorAll<HTMLElement>('[role="status"], [role="alert"]')];
    for (const status of statuses) {
      const text = status.textContent?.trim() ?? "";
      if (text && text !== statusText.get(status)) {
        statusText.set(status, text);
        play(status, [{ opacity: 0.82 }, { opacity: 1 }], 160);
      }
    }
  }

  function flush() {
    frame = 0;
    if (disposed) return;
    root.dataset.lcMotion = !enabled() ? "off" : moving() ? "full" : "reduced";
    for (const element of animations.keys())
      if (!moving() || !root.contains(element)) cancel(element);
    for (const branch of pending) hydrate(branch);
    pending.clear();
    for (const [container, before] of layouts) {
      if (!root.contains(container)) continue;
      for (const element of container.querySelectorAll<HTMLElement>("[data-motion-key]")) {
        const previous = before.get(element.dataset.motionKey ?? "");
        // Capture the old painted position, but measure the new layout without
        // a previous in-flight transform. Rapid repeated changes stay continuous.
        cancel(element);
        const current = element.getBoundingClientRect();
        if (
          !previous ||
          !visible(element) ||
          !current.width ||
          !current.height ||
          element.contains(doc.activeElement)
        )
          continue;
        const dx = previous.left - current.left;
        const dy = previous.top - current.top;
        if (
          Math.abs(dx) + Math.abs(dy) < 1 &&
          previous.width === current.width &&
          previous.height === current.height
        )
          continue;
        play(
          element,
          [
            {
              transform: `translate(${dx}px, ${dy}px) scale(${previous.width / current.width}, ${previous.height / current.height})`,
              transformOrigin: "top left",
            },
            { transform: "translate(0, 0) scale(1, 1)", transformOrigin: "top left" },
          ],
          260,
        );
      }
    }
    layouts.clear();
    updateNavigation();
  }

  function schedule() {
    if (!frame && !disposed) frame = win!.requestAnimationFrame(flush);
  }

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      const target =
        record.target instanceof HTMLElement ? record.target : record.target.parentElement;
      if (!target || target.classList.contains("lc-motion-navigation-ink")) continue;
      if (record.type === "characterData") {
        const status = target.closest<HTMLElement>('[role="status"], [role="alert"]');
        if (status) pending.add(status);
      } else if (record.type === "childList") {
        for (const node of record.addedNodes)
          if (node instanceof HTMLElement && !node.classList.contains("lc-motion-navigation-ink"))
            pending.add(node);
        const status = target.closest<HTMLElement>('[role="status"], [role="alert"]');
        if (status) pending.add(status);
      } else if (record.attributeName === "open") {
        if (target.matches("details[open]")) {
          for (const child of [...target.children]
            .filter((c): c is HTMLElement => c instanceof HTMLElement && c.tagName !== "SUMMARY")
            .slice(0, 4)) {
            play(
              child,
              [
                { opacity: 0.84, translate: "0 -4px" },
                { opacity: 1, translate: "0 0" },
              ],
              200,
            );
          }
        } else if (target.matches("dialog[open]")) {
          visited.delete(target);
          pending.add(target);
        }
      } else if (record.attributeName === "data-motion-view") {
        if (!target.matches(".photo-grid"))
          play(
            target,
            [
              { opacity: 0.88, translate: "10px 0" },
              { opacity: 1, translate: "0 0" },
            ],
            200,
          );
      } else if (
        record.attributeName === "data-motion-turn" ||
        (record.attributeName === "data-semantic-id" && target.matches(".bdr-surface"))
      ) {
        const page = target.matches(".bdr-surface")
          ? target
          : target.querySelector<HTMLElement>(".book-studio__page:not(.book-studio__page--cover)");
        if (page && !page.contains(doc.activeElement))
          play(
            page,
            [
              { clipPath: "inset(0 12% 0 0)", opacity: 0.88 },
              { clipPath: "inset(0)", opacity: 1 },
            ],
            320,
          );
      } else if (
        record.attributeName === "aria-current" &&
        target.matches('.lc-progress-steps > li[aria-current="step"]')
      ) {
        const mark = target.querySelector<HTMLElement>("span");
        if (mark)
          play(
            mark,
            [
              { clipPath: "inset(0 50% 0 0)", opacity: 0.8 },
              { clipPath: "inset(0)", opacity: 1 },
            ],
            200,
          );
      } else if (record.attributeName === "data-motion-scope") {
        pending.add(root);
      }
    }
    schedule();
  });
  observer.observe(root, {
    childList: true,
    characterData: true,
    subtree: true,
    attributes: true,
    attributeFilter: [
      "aria-current",
      "aria-pressed",
      "aria-expanded",
      "open",
      "data-motion-scope",
      "data-motion-view",
      "data-motion-turn",
      "data-semantic-id",
    ],
  });

  function finishForInteraction(event: Event) {
    if (!(event.target instanceof Element)) return;
    for (const [element, animation] of animations) {
      if (element.contains(event.target)) {
        try {
          animation.finish();
        } catch {
          animation.cancel();
        }
        animations.delete(element);
      }
    }
  }

  function change(event: Event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    const choice = event.target.closest<HTMLElement>(
      ".lc-choice-card, .lc-segmented label, .lc-check, .lc-switch, .family-creative__option, .family-creative__response, .photo-card",
    );
    if (choice) play(choice, [{ opacity: 0.76 }, { opacity: 1 }], 140);
  }

  function click(event: Event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest(
      ".book-studio__tabs button, .shared-studio-journey a, .family-creative__mobile-view label",
    );
    if (button) {
      const canvas = button
        .closest("main")
        ?.querySelector<HTMLElement>(".book-studio__canvas, .family-creative__option-deck");
      if (canvas) play(canvas, [{ opacity: 0.86 }, { opacity: 1 }], 180);
    }
    schedule();
  }

  function captureLayout(event: Event) {
    if (!moving() || !(event.target instanceof Element)) return;
    if (event instanceof KeyboardEvent && !["ArrowUp", "ArrowDown"].includes(event.key)) return;
    const photoControl = event.target.closest(
      ".photo-library__views button, .photo-display-toggle button",
    );
    const chapterControl = event.target.closest(
      ".book-studio__rail [data-motion-key], .book-studio__rail button",
    );
    const container = photoControl
      ? photoControl.closest(".photo-library")?.querySelector<HTMLElement>(".photo-grid")
      : chapterControl?.closest(".book-studio__rail")?.querySelector<HTMLElement>("ol");
    if (!container) return;
    const before = new Map<string, DOMRect>();
    for (const element of [...container.querySelectorAll<HTMLElement>("[data-motion-key]")]
      .filter(visible)
      .slice(0, 12)) {
      const rect = element.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < win!.innerHeight)
        before.set(element.dataset.motionKey!, rect);
    }
    layouts.set(container, before);
    schedule();
  }

  function resize() {
    for (const [nav, marker] of markers) {
      if (marker.position !== undefined && nav.style.position === "relative")
        nav.style.position = marker.position;
      marker.position = undefined;
      marker.element.hidden = true;
      cancel(marker.element);
    }
    schedule();
  }

  function visibility() {
    // No loops or resumed entrance choreography when returning to a tab.
    if (doc.hidden) for (const element of animations.keys()) cancel(element);
    schedule();
  }

  root.addEventListener("focusin", finishForInteraction);
  root.addEventListener("pointerdown", finishForInteraction);
  root.addEventListener("change", change);
  root.addEventListener("click", click);
  root.addEventListener("click", captureLayout, true);
  root.addEventListener("keydown", captureLayout, true);
  root.addEventListener("scroll", schedule, true);
  win.addEventListener("resize", resize);
  preference?.addEventListener?.("change", schedule);
  doc.addEventListener("visibilitychange", visibility);
  doc.fonts?.addEventListener?.("loadingdone", schedule);
  flush();

  return () => {
    disposed = true;
    observer.disconnect();
    win.cancelAnimationFrame(frame);
    for (const element of animations.keys()) cancel(element);
    for (const [nav, marker] of markers) {
      marker.element.remove();
      delete nav.dataset.motionNavigation;
      if (marker.position !== undefined && nav.style.position === "relative")
        nav.style.position = marker.position;
    }
    markers.clear();
    pending.clear();
    layouts.clear();
    root.removeEventListener("focusin", finishForInteraction);
    root.removeEventListener("pointerdown", finishForInteraction);
    root.removeEventListener("change", change);
    root.removeEventListener("click", click);
    root.removeEventListener("click", captureLayout, true);
    root.removeEventListener("keydown", captureLayout, true);
    root.removeEventListener("scroll", schedule, true);
    win.removeEventListener("resize", resize);
    preference?.removeEventListener?.("change", schedule);
    doc.removeEventListener("visibilitychange", visibility);
    doc.fonts?.removeEventListener?.("loadingdone", schedule);
    delete root.dataset.lcMotion;
  };
}
